import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchAdminUsers,
  fetchAdminUserDetail,
  updateUserTier,
  suspendUser,
  unsuspendUser,
  setInternalAccess,
  setIsTest,
  impersonateUser,
  fetchGiftLicenses,
  type InternalAccessTier,
  type UserListParams,
} from '@/lib/api/admin-users'

export const adminUserKeys = {
  all: ['admin-users'] as const,
  list: (params: UserListParams) => [...adminUserKeys.all, 'list', params] as const,
  detail: (id: string) => [...adminUserKeys.all, 'detail', id] as const,
}

export function useAdminUserList(params: UserListParams) {
  return useQuery({
    queryKey: adminUserKeys.list(params),
    queryFn: () => fetchAdminUsers(params),
  })
}

export function useAdminUserDetail(userId: string | undefined) {
  return useQuery({
    queryKey: adminUserKeys.detail(userId ?? ''),
    queryFn: () => fetchAdminUserDetail(userId!),
    enabled: !!userId,
  })
}

export function useUpdateUserTier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { userId: string; tier: string; reason: string }) =>
      updateUserTier(vars.userId, vars.tier, vars.reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(vars.userId) })
      qc.invalidateQueries({ queryKey: adminUserKeys.all })
      toast.success('Tier updated successfully')
    },
    onError: (err) => toast.error(`Failed to update tier: ${err.message}`),
  })
}

export function useSuspendUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { userId: string; reason: string }) =>
      suspendUser(vars.userId, vars.reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(vars.userId) })
      qc.invalidateQueries({ queryKey: adminUserKeys.all })
      toast.success('User suspended')
    },
    onError: (err) => toast.error(`Failed to suspend: ${err.message}`),
  })
}

export function useUnsuspendUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { userId: string; reason: string }) =>
      unsuspendUser(vars.userId, vars.reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(vars.userId) })
      qc.invalidateQueries({ queryKey: adminUserKeys.all })
      toast.success('User unsuspended')
    },
    onError: (err) => toast.error(`Failed to unsuspend: ${err.message}`),
  })
}

export function useSetInternalAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { userId: string; enable: boolean; tier?: InternalAccessTier }) =>
      setInternalAccess(vars.userId, vars.enable, vars.tier),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(vars.userId) })
      toast.success(vars.enable ? `Internal access granted (${vars.tier ?? 'pro'})` : 'Internal access revoked')
    },
    onError: (err) => toast.error(`Failed to update internal access: ${err.message}`),
  })
}

export function useSetIsTest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { userId: string; isTest: boolean }) =>
      setIsTest(vars.userId, vars.isTest),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(vars.userId) })
      qc.invalidateQueries({ queryKey: adminUserKeys.all })
      toast.success(vars.isTest ? 'Marked as test user' : 'Unmarked as test user')
    },
    onError: (err) => toast.error(`Failed to update test flag: ${err.message}`),
  })
}

export function useImpersonateUser() {
  return useMutation({
    mutationFn: (vars: { userId: string; reason: string; returnUrl?: string }) =>
      impersonateUser(vars.userId, vars.reason, vars.returnUrl),
    onError: (err) => toast.error(`Failed to generate impersonation link: ${err.message}`),
  })
}

export function useGiftLicenses(params: { page?: number; pageSize?: number; search?: string }) {
  return useQuery({
    queryKey: ['admin-gift-licenses', params],
    queryFn: () => fetchGiftLicenses(params),
    staleTime: 60 * 1000,
  })
}
